import { Controller } from "@hotwired/stimulus"

// Shared fullscreen lightbox used by the attachment preview (expenses) and the
// document preview (document browser): builds the overlay, the download/close
// chrome and the filename caption, and tears everything down on backdrop click,
// the close button, Escape or disconnect. Subclasses only supply how the body is
// filled — see renderBody below.
//
// All user-facing strings arrive as values from the view (t(...)); nothing here
// is translated in JS.
export default class PreviewOverlayBase extends Controller {
  static values = {
    url: String,
    filename: String,
    downloadUrl: String,
    i18nDownload: String,
    i18nClose: String,
  }

  // -- subclass contract -------------------------------------------------

  // Fill `body` (the white panel inside the overlay). Called once, right after
  // the overlay is in the document, so clientWidth/clientHeight are usable.
  renderBody(_body) {
    throw new Error("PreviewOverlayBase subclasses must implement renderBody()")
  }

  // ----------------------------------------------------------------------

  open(event) {
    if (event) event.preventDefault()
    if (!this.urlValue) return

    this.previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    this.overlay = document.createElement("div")
    this.overlay.className = "fixed inset-0 bg-black/80 flex items-center justify-center p-6"
    this.overlay.style.zIndex = "10000"
    this.overlay.innerHTML = this.chromeTemplate()

    this.overlay.addEventListener("click", this.handleClick)
    // Capture phase, and the Escape marked handled: opened from inside a modal (the billing
    // send dialog), the modal's own Escape listener on document would otherwise close it too.
    document.addEventListener("keydown", this.handleKeydown, true)
    document.body.appendChild(this.overlay)

    // Focus moves into the overlay, which lives on document.body — outside whatever
    // opened it. Without this the Escape that closes the lightbox keeps bubbling
    // through the trigger's own ancestors: opened from an inline row editor it would
    // close the overlay AND cancel the edit, discarding unsaved fields.
    this.previouslyFocused = document.activeElement
    this.overlay.querySelector("[data-preview-close]")?.focus()

    this.renderBody(this.overlay.querySelector("[data-preview-body]"))
  }

  // Panel classes differ per subclass: an image centres in a shrink-to-fit box,
  // a paged document scrolls inside a fixed-height one.
  get bodyClass() {
    return "flex max-h-[85vh] w-full items-center justify-center overflow-auto rounded bg-white shadow-2xl"
  }

  chromeTemplate() {
    const filename = this.escape(this.filenameValue || "")
    const downloadHref = this.downloadUrlValue || this.urlValue
    return `
      <div class="relative flex max-h-full w-full max-w-5xl flex-col items-center">
        <div class="absolute -top-10 right-0 flex items-center gap-3 text-white text-sm">
          <a href="${this.escape(downloadHref)}"
             class="hover:text-green-300 inline-flex items-center gap-1"
             ${this.titleAttribute(this.i18nDownloadValue)}
             download>
            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </a>
          <button type="button"
                  data-preview-close
                  class="hover:text-green-300"
                  ${this.titleAttribute(this.i18nCloseValue)}>
            <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
        <div data-preview-body class="${this.bodyClass}"></div>
        ${
          filename
            ? `<div class="mt-3 max-w-full truncate text-sm text-white/90">${filename}</div>`
            : ""
        }
      </div>
    `
  }

  // Omitted rather than rendered empty when the view passed no label.
  titleAttribute(value) {
    return value ? `title="${this.escape(value)}"` : ""
  }

  // True once the overlay is gone, so async renderers can bail out instead of
  // appending to a detached node.
  get closed() {
    return !this.overlay
  }

  handleClick = (event) => {
    if (event.target === this.overlay) {
      this.close()
      return
    }
    if (event.target.closest("[data-preview-close]")) {
      event.preventDefault()
      this.close()
      return
    }
    this.handleBodyClick(event)
  }

  // Hook for subclasses that render their own controls inside the overlay. The overlay
  // is appended to document.body, outside the controller's element, so Stimulus
  // data-action never binds in there — everything clickable is delegated from here.
  handleBodyClick(_event) {}

  handleKeydown = (event) => {
    if (event.key !== "Escape") return

    event.preventDefault()
    this.close()
  }

  close() {
    if (!this.overlay) return
    this.overlay.removeEventListener("click", this.handleClick)
    document.removeEventListener("keydown", this.handleKeydown, true)
    this.overlay.remove()
    this.overlay = null
    document.body.style.overflow = this.previousOverflow || ""

    // Back to whatever opened the overlay, so keyboard users do not land at the top
    // of the document. Skipped when that element left the DOM meanwhile.
    if (this.previouslyFocused?.isConnected) this.previouslyFocused.focus()
    this.previouslyFocused = null

    this.teardown()
  }

  // Hook for subclasses holding resources that outlive the DOM node.
  teardown() {}

  disconnect() {
    this.close()
  }

  escape(value) {
    const div = document.createElement("div")
    div.textContent = value
    return div.innerHTML
  }
}
